import { VariableSizeList as List } from "react-window";
import { useMemo, useRef, useEffect } from "react";

const OptimizedUserList = ({ Manage, searchQuery, selectedUsers, handleToggleUser, userspecData }) => {
  const listRef = useRef(null);
  const userSpecialitiesMap = useMemo(() => {
    return userspecData.reduce((acc, item) => {
      if (!acc[item.physician_id]) {
        acc[item.physician_id] = { specialities: [], institutions: [] };
      }
      acc[item.physician_id].specialities.push(item.spec);
      acc[item.physician_id].institutions.push(item.inst);
      return acc;
    }, {});
  }, [userspecData]);

  const searchTerms = useMemo(() => {
    return searchQuery
      .toLowerCase()
      .split(/\s+/)
      .filter(term => term.trim() !== '');
  }, [searchQuery]);

  const filteredUsers = useMemo(() => {
    if (searchTerms.length === 0) return Manage;
  
    return Manage.filter((user) => {
      const { specialities = [], institutions = [] } = userSpecialitiesMap[user.physician_id] || {};
  
      // Transformă toate câmpurile în array de cuvinte mici
      const searchFields = [
        ...user.user_name_surname.toLowerCase().split(/\s+/),
        ...specialities.flatMap(s => s.toLowerCase().split(/\s+/)),
        ...institutions.flatMap(i => i.toLowerCase().split(/\s+/))
      ].filter(Boolean);
  
      // Fiecare termen introdus trebuie să se regăsească într-un cuvânt din câmpuri
      return searchTerms.every(term =>
        searchFields.some(field => field.includes(term))
      );
    });
  }, [Manage, searchTerms, userSpecialitiesMap]);
  
  const rowHeights = useMemo(() => {
    return filteredUsers.map((user) => {
      const userSpecialities = userspecData
        .filter((item) => item.physician_id === user.physician_id)
        .map((item) => item.spec);

      const userInstitutions = userspecData
        .filter((item) => item.physician_id === user.physician_id)
        .map((item) => item.inst);

      const lineCount = userSpecialities.length + userInstitutions.length;

      return 50 + lineCount * 20;
    });
  }, [filteredUsers, userspecData]);

  const getItemSize = (index) => rowHeights[index];

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [filteredUsers]);

  const Row = ({ index, style }) => {
    const user = filteredUsers[index];
    if (!user) return null;

    const userSpecialities = userspecData
      .filter((item) => item.physician_id === user.physician_id)
      .map((item) => item.spec);

    const userInstitutions = userspecData
      .filter((item) => item.physician_id === user.physician_id)
      .map((item) => item.inst);

    return (
      <div style={style} className="flex items-center gap-2 p-2 border-b">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={selectedUsers.includes(user.physician_id)}
          onChange={() => handleToggleUser(user.physician_id, "physician")}
        />
        <div>
          <h3 className="text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
            <span>{user.user_name_surname}</span>
            {(user.data_acord === 1 || user.data_acord === true) && (
              <span title="Acord marketing confirmat" className="inline-flex items-center text-emerald-500 flex-shrink-0">
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </h3>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {userSpecialities.map((spec, index) => (
              <div key={`${spec}-${index}`}>
                {spec} {" - "}
                {userInstitutions[index] || "N/A"}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <List
      ref={listRef}
      height={300}
      itemCount={filteredUsers.length}
      itemSize={getItemSize}
      width={"100%"}
    >
      {Row}
    </List>
  );
};

export default OptimizedUserList;